import { useEffect, useRef, useState } from 'react'
import Loading from '../Loading/Loading'
import './PullToRefresh.css'

const THRESHOLD = 72
const MAX_PULL = 108
const MIN_SPIN_MS = 450

function isField(target) {
  return Boolean(target.closest?.('input, textarea, select, [contenteditable="true"]'))
}

function canScroll(node) {
  const style = window.getComputedStyle(node)
  const overflowY = style.overflowY
  if (overflowY !== 'auto' && overflowY !== 'scroll' && overflowY !== 'overlay') return false
  return node.scrollHeight > node.clientHeight + 1
}

function atTop(target, root) {
  let node = target
  while (node && node !== root) {
    if (canScroll(node) && node.scrollTop > 0) return false
    node = node.parentElement
  }
  const scrolled = window.scrollY || document.documentElement.scrollTop || document.body.scrollTop || 0
  return scrolled <= 0
}

export default function PullToRefresh({ onRefresh, children }) {
  const rootRef = useRef(null)
  const onRefreshRef = useRef(onRefresh)
  const startY = useRef(0)
  const startX = useRef(0)
  const pulling = useRef(false)
  const decided = useRef(false)
  const pullRef = useRef(0)
  const refreshingRef = useRef(false)
  const mountedRef = useRef(true)
  const [pull, setPull] = useState(0)
  const [dragging, setDragging] = useState(false)
  const [refreshing, setRefreshing] = useState(false)

  onRefreshRef.current = onRefresh

  useEffect(() => {
    const root = rootRef.current
    if (!root) return undefined
    mountedRef.current = true

    const reset = () => {
      pulling.current = false
      decided.current = false
      pullRef.current = 0
      setDragging(false)
      setPull(0)
    }

    const onStart = (event) => {
      if (refreshingRef.current || event.touches.length !== 1) return
      const target = event.target
      if (!(target instanceof Element) || !root.contains(target) || isField(target)) return
      if (!atTop(target, root)) return
      startY.current = event.touches[0].clientY
      startX.current = event.touches[0].clientX
      pulling.current = true
      decided.current = false
    }

    const onMove = (event) => {
      if (!pulling.current || refreshingRef.current) return
      const touch = event.touches[0]
      const dx = touch.clientX - startX.current
      const dy = touch.clientY - startY.current
      if (!decided.current) {
        if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return
        decided.current = true
        if (dy <= 0 || Math.abs(dx) > dy) {
          reset()
          return
        }
        setDragging(true)
      }
      if (!atTop(event.target, root)) {
        reset()
        return
      }
      const next = Math.min(MAX_PULL, dy * 0.45)
      pullRef.current = next
      setPull(next)
      if (next > 0) event.preventDefault()
    }

    const onEnd = async () => {
      if (!pulling.current) return
      const distance = pullRef.current
      pulling.current = false
      decided.current = false
      setDragging(false)
      if (distance < THRESHOLD) {
        pullRef.current = 0
        setPull(0)
        return
      }
      refreshingRef.current = true
      setRefreshing(true)
      pullRef.current = THRESHOLD
      setPull(THRESHOLD)
      const started = Date.now()
      try {
        await onRefreshRef.current?.()
      } finally {
        const wait = MIN_SPIN_MS - (Date.now() - started)
        if (wait > 0) await new Promise((resolve) => setTimeout(resolve, wait))
        refreshingRef.current = false
        if (!mountedRef.current) return
        setRefreshing(false)
        pullRef.current = 0
        setPull(0)
      }
    }

    root.addEventListener('touchstart', onStart, { passive: true })
    root.addEventListener('touchmove', onMove, { passive: false })
    root.addEventListener('touchend', onEnd)
    root.addEventListener('touchcancel', onEnd)
    return () => {
      mountedRef.current = false
      root.removeEventListener('touchstart', onStart)
      root.removeEventListener('touchmove', onMove)
      root.removeEventListener('touchend', onEnd)
      root.removeEventListener('touchcancel', onEnd)
    }
  }, [])

  const slot = refreshing ? THRESHOLD : pull

  return (
    <div ref={rootRef} className={`pull-to-refresh${dragging ? ' is-dragging' : ''}`}>
      <div
        className="pull-to-refresh__indicator"
        style={{ height: slot }}
        aria-hidden={slot === 0}
      >
        {slot > 12 ? <Loading size="small" /> : null}
      </div>
      <div className="pull-to-refresh__content">
        {children}
      </div>
    </div>
  )
}
